import { isDefined } from "@clipboard-health/util-ts";
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonList,
  IonLoading,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { api } from "@src/app/api";
import { instantPayStatus, isHCPInstantPayAllowed } from "@src/app/dayView/api";
import { getNotificationPreference } from "@src/app/privacyPolicy/notificationPreference/api";
import { Store } from "@src/app/store/store.model";
import { useToast } from "@src/appV2/lib";
import { logApiFailureEvent } from "@src/lib/analytics";
import { Shift } from "@src/lib/interface/src";
import { chevronBackOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getOpenNegotiatingShifts } from "./api";
import { IRateNegotiationsListProps } from "./interfaces";
import { RateNegotiatingShiftsCard } from "./NegotiatingShiftsCard";
import { useSession } from "../store/helperHooks";

export function RateNegotiationsList(props: IRateNegotiationsListProps) {
  const { onClose } = props;

  const [negotiationShifts, setNegotiationShifts] = useState<Shift[]>([]);
  const [isHcpInstantPayEnabled, setIsHcpInstantPayEnabled] = useState(false);
  const [pushNotification, setPushNotification] = useState<{
    [key in string]: boolean;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [foundDeepLinkNegotiation, setFoundDeepLinkNegotiation] = useState<boolean>(true);

  const { agent } = useSession();
  const { isPushSet } = useSelector((state: Store) => state.notificationStore);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const deepLinkNegotiationId = searchParams.get("negotiationId") ?? props.deepLinkNegotiationId;
  const { showErrorToast } = useToast();

  useEffect(() => {
    (async function () {
      const response = await Promise.all([
        api.env.fetchEnvVariablesWithPushNotificationOnAccountTab(),
        getNotificationPreference(),
      ]);
      setPushNotification({
        accepted: response[1]?.data?.notificationPreferences?.push,
        pushFlag: response[0]?.PushNotificationOnAccountTab as boolean,
        isNotificationAlertShown: agent?.isNotificationAlertShown ?? false,
        isPushSet,
      });
    })();
  }, [setPushNotification, agent, isPushSet]);

  const fetchNegotiatingShifts = async () => {
    try {
      setLoading(true);
      const negotiatingShifts = await getOpenNegotiatingShifts();
      setNegotiationShifts(negotiatingShifts);
    } catch (error) {
      logApiFailureEvent(error);
      showErrorToast("Error while getting negotiating shifts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNegotiatingShifts();
    fetchHcpInstantPayStatus();
  }, []);

  const fetchHcpInstantPayStatus = async () => {
    const status = await instantPayStatus();
    setIsHcpInstantPayEnabled(isHCPInstantPayAllowed(status));
  };

  useEffect(() => {
    if (deepLinkNegotiationId) {
      const foundNegotiation = negotiationShifts.some((negotiationShift) => {
        return negotiationShift.rateNegotiation?._id === deepLinkNegotiationId;
      });
      setFoundDeepLinkNegotiation(foundNegotiation);
    }
  }, [negotiationShifts, deepLinkNegotiationId]);

  return (
    <IonPage>
      <IonHeader no-border>
        <IonToolbar className="relative" mode="ios">
          <IonButtons align-center>
            {isDefined(onClose) ? (
              <IonButton routerDirection="back" fill="clear" color="light" onClick={onClose}>
                <IonIcon icon={chevronBackOutline} mode="ios" size="large" />
              </IonButton>
            ) : (
              <IonBackButton text="" defaultHref="/home/myShifts" mode="ios" />
            )}
          </IonButtons>
          <IonTitle>Shifts in Negotiation</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonLoading isOpen={loading} backdropDismiss={true} data-testid="negotiations-list-loader" />
      <IonContent>
        <IonList lines="none">
          {negotiationShifts.map((negotiationShift) => {
            return (
              <RateNegotiatingShiftsCard
                negotiationShift={negotiationShift}
                fetchNegotiatingShifts={fetchNegotiatingShifts}
                isHcpInstantPayEnabled={isHcpInstantPayEnabled}
                pushNotification={pushNotification}
                shouldOpenNegotiationTool={
                  !!(
                    deepLinkNegotiationId &&
                    negotiationShift.rateNegotiation?._id === deepLinkNegotiationId
                  )
                }
                key={negotiationShift._id}
              />
            );
          })}
        </IonList>
      </IonContent>
      <IonAlert
        mode="ios"
        isOpen={!foundDeepLinkNegotiation && !loading}
        header="Negotiation not found"
        message="Sorry, that notification cannot be found in our system. You can try to find it in My Shifts, but negotiations are only visible for a limited time after they end."
        backdropDismiss={false}
        buttons={[
          {
            text: "OK",
            handler: () => setFoundDeepLinkNegotiation(true),
          },
        ]}
      />
    </IonPage>
  );
}
